import { singletonQueryClient } from '$utils'
import { stockApi } from '$api'
import dayjs from 'dayjs'
import { StockCostResponse, PriceType } from '$models'

enum QueryKeys {
  getStockCost = 'stock-cost',
  getStockCostForMenu = 'menu-cost',
}

/**
 * Retrieves the stock costs of the given items and recipes, or all items in a given
 * stocktake.
 *
 * @param [optional] itemIdTimeStrings An Array of comma seperated strings of the form {itemID},{timeItemShouldBeCostedAt}
 * @param [optional] recipeIdTimeStrings An Array of comma seperated strings of the form {recipeID},{timeRecipeShouldBeCostedAt}
 * @param [optional] stocktakeId A stocktake ID
 * @returns StockCostResponse holding the costs of all items and recipes passed in the request
 */
export const getStockCost = async (
  itemIdTimeStrings?: string[],
  recipeIdTimeStrings?: string[],
  variantIdTimeStrings?: string[],
  stocktakeId?: string,
  useForecastPrice: boolean = false
): Promise<StockCostResponse> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getStockCost,
      {
        itemIdTimeStrings,
        recipeIdTimeStrings,
        variantIdTimeStrings,
        stocktakeId,
        useForecastPrice,
      },
    ],
    queryFn: async ({ signal }) => {
      return await stockApi.getStockCost(
        formatTimesInIdTimeStrings(itemIdTimeStrings),
        formatTimesInIdTimeStrings(recipeIdTimeStrings),
        formatTimesInIdTimeStrings(variantIdTimeStrings),
        stocktakeId,
        useForecastPrice ? PriceType.Forecast : PriceType.Live,
        signal
      )
    },
  })
}

export const getStockCostForMenu = async (
  menuId: string,
  priceType: PriceType
): Promise<StockCostResponse> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getStockCostForMenu,
      {
        menuId,
        priceType,
      },
    ],
    queryFn: async ({ signal }) => {
      return await stockApi.getMenuCost(menuId, priceType, signal)
    },
  })
}

const formatTimesInIdTimeStrings = (
  idTimeStrings: string[] | undefined
): string[] => {
  if (idTimeStrings == null) return []

  const formattedStringsSet = new Set<string>()

  idTimeStrings.forEach((string) => {
    const split = string.split(',')
    const timeFormattedTOISO8601 = dayjs(split[1]).toISOString()

    formattedStringsSet.add(`${split[0]},${timeFormattedTOISO8601}`)
  })

  return Array.from(formattedStringsSet)
}
